import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, type TournamentSetupStatus } from "../lib/api";
import { DraftTournamentContext } from "../lib/draftTournamentContext";
import { DRAFT_NOTICE_TEXT, draftSectionDestination, draftWizardDestination } from "../lib/draftWizard";
import AdminTournamentOperationPanel from "./AdminTournamentOperationPanel";

interface Props {
  children: ReactNode;
  setupOnly?: boolean;
  allowIncompleteAccess?: boolean;
}

type GuardState =
  | { kind: "checking"; path: string }
  | { kind: "ready"; path: string; setupStatus: TournamentSetupStatus | null; closed?: boolean; draft?: boolean }
  | { kind: "error"; path: string };

/** Borrador regresado al asistente (Eduardo 2026-10-02): aviso fijo arriba
 * del paso al que se le mandó, mientras siga en esa página. */
function DraftTournamentNotice() {
  return (
    <section className="card tournament-closed-banner tournament-draft-notice" data-no-toast role="status">
      {DRAFT_NOTICE_TEXT}
    </section>
  );
}

/** Torneo cerrado (Eduardo 2026-09-27): aviso fijo de solo lectura arriba de
 * cada página del torneo (el Resumen trae la tarjeta completa). */
function ClosedTournamentBanner({ tournamentId, superadmin }: { tournamentId: number; superadmin: boolean }) {
  if (superadmin) {
    return (
      <section className="card tournament-closed-banner" data-no-toast role="status">
        <strong>Torneo cerrado.</strong>{" "}
        <span>
          Como superadmin puedes corregirlo sin reabrir: cada cambio queda en la bitácora, no se avisa a los jugadores y
          las posiciones finales se recalculan (el ranking sigue congelado).
        </span>
      </section>
    );
  }
  return (
    <section className="card tournament-closed-banner" data-no-toast role="status">
      <strong>Torneo cerrado · solo lectura.</strong>{" "}
      <span>Los cambios están bloqueados; pagos, reembolsos y comunicados siguen disponibles.</span>{" "}
      <Link to={`/admin/tournaments/${tournamentId}`}>Ir al Resumen para reabrirlo</Link>
    </section>
  );
}


export default function TournamentSetupGuard({
  children,
  setupOnly = false,
  allowIncompleteAccess = false,
}: Props) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token, hasRole, isImpersonating } = useAuth();
  const location = useLocation();

  const [state, setState] = useState<GuardState>({
    kind: "checking",
    path: location.pathname,
  });

  useEffect(() => {
    const path = location.pathname;
    if (!token || Number.isNaN(tournamentId)) {
      setState({ kind: "ready", path, setupStatus: null });
      return;
    }
    let cancelled = false;
    setState({ kind: "checking", path });
    void (async () => {
      try {
        const response = await api.tournaments.get(token, tournamentId);
        if (cancelled) return;
        setState({
          kind: "ready",
          path,
          setupStatus: response.data.setup_status,
          closed: response.data.tournament.status === "closed",
          draft: response.data.tournament.status === "draft",
        });
      } catch {
        if (cancelled) return;
        // Let the wrapped page surface the error itself.
        setState({ kind: "error", path });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, location.pathname]);

  if (state.path !== location.pathname || state.kind === "checking") {
    return (
      <section className="card">
        <p className="muted">Cargando…</p>
      </section>
    );
  }

  const isHome = /^\/admin\/tournaments\/\d+\/?$/.test(location.pathname);
  const isDraft = state.kind === "ready" && Boolean(state.draft);
  const withDraftContext = (content: ReactNode) => (
    <DraftTournamentContext.Provider value={isDraft}>{content}</DraftTournamentContext.Provider>
  );
  const banner =
    state.kind === "ready" && state.closed && !isHome ? (
      <ClosedTournamentBanner tournamentId={tournamentId} superadmin={hasRole("superadmin") && !isImpersonating} />
    ) : null;

  if (setupOnly) {
    // DENTRO del wizard hay UNA sola autoridad de navegación: los candados
    // del stepper (compuertas por categoría de setupGates). Este guard
    // también rebotaba al `next_setup_path` del backend cuando estabas en un
    // paso posterior, y como su parser de pasos no entendía `?division=N`,
    // el rebote era errático: "voy a Draw y me saca del wizard" / "después
    // del Draw no pasa por Comunicación" (reporte Eduardo 2026-07-15). El
    // rebote intra-wizard se elimina; para rutas fuera del wizard (abajo)
    // el guard sigue redirigiendo al paso pendiente.
    const draftNotice =
      state.kind === "ready" &&
      state.draft &&
      Boolean((location.state as { draftNotice?: boolean } | null)?.draftNotice);
    return withDraftContext(
      <>
        {banner}
        {draftNotice ? <DraftTournamentNotice /> : null}
        {children}
      </>,
    );
  }

  // Eduardo 2026-09-22: desde el paso Comunicación del wizard se puede ir a
  // Comunicados a VER cómo le llega cada aviso al jugador (?from=wizard);
  // esa pantalla trae "Regresar a Comunicación" y no saca del wizard.
  // También la liga de «Enviármelo de prueba» (?aviso=, push o bandeja):
  // con el torneo en borrador debe abrir ese aviso, no un paso del wizard.
  const searchParams = new URLSearchParams(location.search);
  const viewingFromWizard =
    location.pathname.endsWith("/communications") &&
    (searchParams.get("from") === "wizard" || Boolean(searchParams.get("aviso")));

  // Eduardo 2026-10-02: un torneo en BORRADOR se queda en el asistente hasta
  // publicarlo. Antes el guard solo miraba `is_incomplete_setup`, y el
  // backend lo apaga en cuanto todas las categorías tienen draw — el borrador
  // abría el Resumen y el dock «Operación del torneo» (al terminar Calendario
  // o al tocar el checklist). Ahora manda el estado del torneo: Resumen y
  // secciones de operación regresan al paso de Publicar (o al pendiente).
  const inWizard =
    state.kind === "ready" && (Boolean(state.draft) || Boolean(state.setupStatus?.is_incomplete_setup));

  if (!allowIncompleteAccess && !viewingFromWizard && state.kind === "ready" && inWizard) {
    // Revisión del borrador: Calendario, Grupos, Draw, Jugadores y Reglamento
    // son el mismo paso del asistente — se abre ese paso con su categoría y
    // fecha (calendario del club, Inicio, barra de abajo). El Resumen y lo
    // que solo existe publicado regresan a Publicar (o al paso pendiente).
    const target =
      (state.draft
        ? draftSectionDestination(tournamentId, location.pathname, location.search, state.setupStatus)
        : null) ??
      draftWizardDestination(
        tournamentId,
        state.setupStatus,
        new URLSearchParams(location.search).get("division"),
      );
    const current = `${location.pathname}${location.search}`;
    if (target !== current) {
      return <Navigate to={target} replace state={state.draft ? { draftNotice: true } : undefined} />;
    }
  }

  if (!setupOnly && state.kind === "ready" && !inWizard) {
    return withDraftContext(
      <>
        {banner}
        {children}
        <AdminTournamentOperationPanel tournamentId={tournamentId} />
      </>,
    );
  }

  return withDraftContext(
    <>
      {banner}
      {children}
    </>,
  );
}
